import { useEffect, useRef, useState } from "react";

import { Link, useNavigate } from "react-router";
import axios from "../lib/axios";
import { subscribeRealtime } from "../lib/realtimeEvents";
import { useSidebar } from "../context/SidebarContext";
import { useAuth } from "../context/AuthContext";
import { useActiveStore } from "../context/ActiveStoreContext";
import { ThemeToggleButton } from "../components/common/ThemeToggleButton";
// import NotificationDropdown from "../components/header/NotificationDropdown";
import UserDropdown from "../components/header/UserDropdown";
import NotificationBell from "../components/header/NotificationBell";
import StoreSwitcher from "../components/store/StoreSwitcher";
import { IsightOpenButton } from "./IsightOpenButton";
import PendingInvitationsDropdown from "../components/invitation/PendingInvitationsDropdown";
import { TrialExpiryModal } from "../components/common/TrialExpiryModal";


const ISIGHT_BASE_URL =
  import.meta.env.VITE_ISIGHT_URL || "https://isight.netsights.ai";

type CachedCredits = {
  value: number | null;
  ts: number;
  inFlight?: Promise<number | null>;
};



const CREDITS_CACHE_TTL_MS = 30_000;
const creditsCache = new Map<string, CachedCredits>();

// In-memory fallback for private/incognito browsers where localStorage.setItem throws
let inMemoryCreditsFallback: string | null = null;

const setStoredCredits = (v: number | null) => {
  const strValue = String(v ?? 0);
  try {
    localStorage.setItem("netsight_available_credits", strValue);
    inMemoryCreditsFallback = null;
  } catch {
    inMemoryCreditsFallback = strValue;
  }
};

const AppHeader: React.FC<{
  disableNavigation?: boolean;
  /** Full-width bar: no sidebar offset, no hamburger, Neo logo left on all breakpoints (e.g. data sync page). */
  standaloneBar?: boolean;
}> = ({ disableNavigation = false, standaloneBar = false }) => {
  const navigate = useNavigate();
  const { isMobileOpen, toggleSidebar, toggleMobileSidebar } = useSidebar();

  const {
    stores,
    currentShop,
    user,
    currentSubscription,
    loadSubscriptionData,
  } = useAuth();
  const isPaidSubscription =
    Number((currentSubscription as any)?.price ?? 0) > 0;
  const { activeStore, userStores: newStores } = useActiveStore();

  // Check if user is member and has stores (regardless of connection status)
  const isMemberUser = newStores.some(store => store.user_role === 'member');
  const hasMultipleStores = newStores.length > 1;
  const hasAnyStore = newStores.length > 0;
  const hasConnectedStore = newStores.some(store => store.connected);
  // Removed excessive debug logging to prevent system hanging
  // console.log("Has multi store",hasMultipleStores);

  const [showPopup, setShowPopup] = useState(false);
  const [availableCredits, setAvailableCredits] = useState<number | null>(null);
  const [isightError, setIsightError] = useState<string | null>(null);
  const [showTrialModal, setShowTrialModal] = useState(false);

  // Show trial expiry modal once per login session (resets on logout / new tab)
  // useEffect(() => {
  //   const days = currentSubscription?.trial_days_remaining;
  //   if (typeof days === "number" && days >= 0 && days <= 7) {
  //     const alreadyShown = sessionStorage.getItem("trial_modal_shown");
  //     if (!alreadyShown) {
  //       setShowTrialModal(true);
  //       sessionStorage.setItem("trial_modal_shown", "1");
  //     }
  //   }
  // }, [currentSubscription?.trial_days_remaining]);
  useEffect(() => {
    const days = currentSubscription?.trial_days_remaining;
    if (!isPaidSubscription && typeof days === "number" && days >= 0 && days <= 7) {
      const alreadyShown = sessionStorage.getItem("trial_modal_shown");
      if (!alreadyShown) {
        setShowTrialModal(true);
        sessionStorage.setItem("trial_modal_shown", "1");
      }
    }
  }, [currentSubscription?.trial_days_remaining, isPaidSubscription]);

  // Ensure subscription data is loaded (for pages outside AppLayout like /notification, /alert)
  useEffect(() => {
    if (currentShop && !currentSubscription) {
      loadSubscriptionData(currentShop);
    }
  }, [currentShop, currentSubscription, loadSubscriptionData]);

  const handleToggle = () => {
    if (window.innerWidth >= 991) {
      toggleSidebar();
    } else {
      toggleMobileSidebar();
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Load merchant credits for current shop so header can show dynamic value
  useEffect(() => {
    let isAlive = true;

    // const loadCredits = async (forceFresh = false) => {
    //   if (!currentShop) {
    //     setAvailableCredits(null);
    //     return;
    //   }
    //   const cached = creditsCache.get(currentShop);
    //   const now = Date.now();

    //   // Fast path: use cached value immediately (prevents flicker on remount)
    //   if (!forceFresh && cached && now - cached.ts < CREDITS_CACHE_TTL_MS) {
    //     setAvailableCredits(cached.value);
    //     // Still reuse in-flight (if any) to refresh silently
    //     if (cached.inFlight) {
    //       cached.inFlight.then((v) => {
    //         if (isAlive) setAvailableCredits(v);
    //       });
    //     }
    //     return;
    //   }

    //   // If a request is already running for this shop, reuse it
    //   if (cached?.inFlight) {
    //     const v = await cached.inFlight;
    //     if (isAlive) setAvailableCredits(v);
    //     return;
    //   }

    //   try {
    //     const inFlight = axios
    //       .get("/merchant-credits", { params: { shop: currentShop } })
    //       .then((res) => (res.data?.available_credits ?? null) as number | null)
    //       .catch(() => null)
    //       .finally(() => {
    //         const latest = creditsCache.get(currentShop);
    //         if (latest) {
    //           delete latest.inFlight;
    //         }
    //       });

    //     creditsCache.set(currentShop, {
    //       value: cached?.value ?? null,
    //       ts: cached?.ts ?? 0,
    //       inFlight,
    //     });

    //     const value = await inFlight;
    //     creditsCache.set(currentShop, { value, ts: Date.now() });
    //     if (isAlive) setAvailableCredits(value);
    //   } catch {
    //     // Don't blow away existing credits on transient errors.
    //     // Keep last known good value (prevents showing "0" flicker).
    //     const last = creditsCache.get(currentShop)?.value ?? null;
    //     if (isAlive && last !== null) {
    //       setAvailableCredits(last);
    //     }
    //   }
    // };

    const loadCredits = async (forceFresh = false) => {
      if (!currentShop) {
        setAvailableCredits(null);

        // clear localStorage too
        try {
          localStorage.setItem("netsight_available_credits", "0");
        } catch {
          /* ignore */
        }

        return;
      }

      const cached = creditsCache.get(currentShop);
      const now = Date.now();

      // Fast path: use cached value immediately
      if (!forceFresh && cached && now - cached.ts < CREDITS_CACHE_TTL_MS) {
        setAvailableCredits(cached.value);

        // SAVE TO LOCALSTORAGE
        try {
          localStorage.setItem(
            "netsight_available_credits",
            String(cached.value ?? 0)
          );
        } catch {
          /* ignore */
        }

        // Still reuse in-flight (if any) to refresh silently
        if (cached.inFlight) {
          cached.inFlight.then((v) => {
            if (isAlive) {
              setAvailableCredits(v);

              // UPDATE LOCALSTORAGE
              try {
                localStorage.setItem(
                  "netsight_available_credits",
                  String(v ?? 0)
                );
              } catch {
                /* ignore */
              }
            }
          });
        }

        return;
      }

      // If a request is already running for this shop, reuse it
      if (cached?.inFlight) {
        const v = await cached.inFlight;

        if (isAlive) {
          setAvailableCredits(v);

          // SAVE TO LOCALSTORAGE
          try {
            localStorage.setItem(
              "netsight_available_credits",
              String(v ?? 0)
            );
          } catch {
            /* ignore */
          }
        }

        return;
      }

      try {
        const inFlight = axios
          .get("/merchant-credits", { params: { shop: currentShop } })
          .then((res) => (res.data?.available_credits ?? null) as number | null)
          .catch(() => null)
          .finally(() => {
            const latest = creditsCache.get(currentShop);

            if (latest) {
              delete latest.inFlight;
            }
          });

        creditsCache.set(currentShop, {
          value: cached?.value ?? null,
          ts: cached?.ts ?? 0,
          inFlight,
        });

        const value = await inFlight;

        creditsCache.set(currentShop, {
          value,
          ts: Date.now(),
        });

        if (isAlive) {
          setAvailableCredits(value);

          // SAVE TO LOCALSTORAGE
          try {
            localStorage.setItem(
              "netsight_available_credits",
              String(value ?? 0)
            );
          } catch {
            /* ignore */
          }
        }
      } catch {
        // Keep last known value
        const last = creditsCache.get(currentShop)?.value ?? null;

        if (isAlive && last !== null) {
          setAvailableCredits(last);

          // SAVE FALLBACK VALUE
          try {
            localStorage.setItem(
              "netsight_available_credits",
              String(last ?? 0)
            );
          } catch {
            /* ignore */
          }
        }
      }
    };

    void loadCredits();
    const handleWindowFocus = () => {
      void loadCredits();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void loadCredits();
      }
    };
    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const applyCredits = (credits: number | null) => {
      setAvailableCredits(credits);
      creditsCache.set(currentShop, { value: credits, ts: Date.now() });
      setStoredCredits(credits);
    };

    // Listen for local updates and backend broadcasts instead of polling every few seconds.
    const handleCreditUpdate = (event: CustomEvent) => {
      if (event.detail?.shop === currentShop) {
        applyCredits(event.detail.credits ?? null);
      }
    };

    const unsubscribeRealtime = subscribeRealtime<{ available_credits?: number; credits?: number }>(
      {
        "credits.updated": (payload) => applyCredits(payload.available_credits ?? payload.credits ?? null),
        "merchant.credits.updated": (payload) => applyCredits(payload.available_credits ?? payload.credits ?? null),
      },
      { shop: currentShop },
    );
    window.addEventListener(
      "creditsUpdated",
      handleCreditUpdate as EventListener,
    );
    return () => {
      isAlive = false;
      unsubscribeRealtime();
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener(
        "creditsUpdated",
        handleCreditUpdate as EventListener,
      );
    };
  }, [currentShop]);

  /**
   * Open iSight Intelligence Hub.
   * Since we now have a background handshake and cookie persistence, we go direct.
   * iSight will automatically handle authentication if the session is missing.
   */
  const handleOpenIsight = () => {
    const shop = currentShop || localStorage.getItem("shop");

    if (!shop) {
      setIsightError("Please select a store first.");
      return;
    }

    // Direct redirect. iSight's loader will handle the auth check.
    window.location.href = `${ISIGHT_BASE_URL}/intelligence?shop=${encodeURIComponent(shop)}`;
  };



  return (
    <>
      {/* Fixed header out of flow: 48px bar, edge to edge */}
      <div className="h-12 shrink-0" aria-hidden />
      <header
        className="fixed top-0 left-0 right-0 z-50 flex shrink-0 flex-col border-t-2 border-transparent bg-[var(--page)]"
        style={{ borderImage: "var(--grad) 1" }}
      >
        <div className="flex min-h-0 h-12 w-full flex-grow items-center justify-between gap-2 border-b border-[var(--rule)] px-2 sm:gap-4 sm:px-4 overflow-visible">
          <div className="relative flex flex-1 min-w-0 items-center gap-2 sm:gap-4 lg:justify-normal">
            {standaloneBar ? (
              <Link
                to="/"
                className="relative z-10 flex shrink-0 items-center py-1 pl-1 pr-3"
                aria-label="Neo console home"
              >
                <img
                  className="h-[22px] w-auto"
                  src="/images/neo-logo/neo-logo.svg"
                  alt="Neo"
                />
              </Link>
            ) : (
              <>
                {/* <button
                  className="z-99999 flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full border border-[var(--rule)] text-[var(--mute)] transition-colors hover:bg-[var(--band)] hover:text-[var(--ink)]"
                  onClick={handleToggle}
                  aria-label="Toggle Sidebar"
                >
                  {isMobileOpen ? (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M6.21967 7.28131C5.92678 6.98841 5.92678 6.51354 6.21967 6.22065C6.51256 5.92775 6.98744 5.92775 7.28033 6.22065L11.999 10.9393L16.7176 6.22078C17.0105 5.92789 17.4854 5.92788 17.7782 6.22078C18.0711 6.51367 18.0711 6.98855 17.7782 7.28144L13.0597 12L17.7782 16.7186C18.0711 17.0115 18.0711 17.4863 17.7782 17.7792C17.4854 18.0721 17.0105 18.0721 16.7176 17.7792L11.999 13.0607L7.28033 17.7794C6.98744 18.0722 6.51256 18.0722 6.21967 17.7794C5.92678 17.4865 5.92678 17.0116 6.21967 16.7187L10.9384 12L6.21967 7.28131Z"
                        fill="currentColor"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 16 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0.583252 1C0.583252 0.585788 0.919038 0.25 1.33325 0.25H14.6666C15.0808 0.25 15.4166 0.585786 15.4166 1C15.4166 1.41421 15.0808 1.75 14.6666 1.75L1.33325 1.75C0.919038 1.75 0.583252 1.41422 0.583252 1ZM0.583252 11C0.583252 10.5858 0.919038 10.25 1.33325 10.25L14.6666 10.25C15.0808 10.25 15.4166 10.5858 15.4166 11C15.4166 11.4142 15.0808 11.75 14.6666 11.75L1.33325 11.75C0.919038 11.75 0.583252 11.4142 0.583252 11ZM1.33325 5.25C0.919038 5.25 0.583252 5.58579 0.583252 6C0.583252 6.41421 0.919038 6.75 1.33325 6.75L7.99992 6.75C8.41413 6.75 8.74992 6.41421 8.74992 6C8.74992 5.58579 8.41413 5.25 7.99992 5.25L1.33325 5.25Z"
                        fill="currentColor"
                      />
                    </svg>
                  )}
                </button> */}
                {/* {(() => {
                const days = currentSubscription?.trial_days_remaining;
                if (typeof days === "number" && days >= 0 && days <= 7) {
                  return (
<div className="hidden w-full py-2 bg-transparent text-center text-[11px] font-bold text-[var(--accent2)] animate-pulse dark:text-[var(--accent2)] lg:block">
                      Your free trial expires in {days} days
</div>
                  );
                }
                return null;
              })()} */}
                {/* {(() => {
                  const days = currentSubscription?.trial_days_remaining;
                  const isTrialEnded = days === 0;

                  if (typeof days === "number" && days >= 0 && days <= 7) {
                    return (
                      <div
                        className={`hidden w-full py-2 bg-transparent text-center text-[11px] font-bold lg:block ${isTrialEnded
                          ? "text-red-600 dark:text-red-400"
                          : "text-[var(--accent2)] animate-pulse dark:text-[var(--accent2)]"
                          }`}
                      >
                        {isTrialEnded
                          ? "Your trial period has ended. "
                          : `Your free trial expires in ${days} day${days !== 1 ? "s" : ""
                          }. `}

                        <span
                          onClick={() => navigate("/billing")}
                          className="cursor-pointer underline hover:opacity-80"
                        >
                          Upgrade now
                        </span>

                        <button
                          type="button"
                          onClick={() => setShowTrialModal(true)}
                          aria-label="Trial info"
                          className="ml-3 inline-flex h-5 w-5 items-center justify-center rounded-full border border-current text-[9px] font-bold leading-none align-middle"
                        >
                          i
                        </button>
                      </div>
                    );
                  }

                  return null;
                })()} */}
                {(() => {
                  const days = currentSubscription?.trial_days_remaining;
                  const isTrialEnded = days === 0;

                  if (!isPaidSubscription && typeof days === "number" && days >= 0 && days <= 7) {
                    return (
                      <div
                        className={`hidden w-full py-2 bg-transparent text-center text-[11px] font-bold lg:block ${isTrialEnded
                          ? "text-red-600 dark:text-red-400"
                          : "text-[var(--accent2)] animate-pulse dark:text-[var(--accent2)]"
                          }`}
                      >
                        {isTrialEnded
                          ? "Your trial period has ended. "
                          : `Your free trial expires in ${days} day${days !== 1 ? "s" : ""
                          }. `}

                        <span
                          onClick={() => navigate("/billing")}
                          className="cursor-pointer underline hover:opacity-80"
                        >
                          Upgrade now
                        </span>

                        <button
                          type="button"
                          onClick={() => setShowTrialModal(true)}
                          aria-label="Trial info"
                          className="ml-3 inline-flex h-5 w-5 items-center justify-center rounded-full border border-current text-[9px] font-bold leading-none align-middle"
                        >
                          i
                        </button>
                      </div>
                    );
                  }

                  return null;
                })()}
                {/* Brand - desktop */}
                <Link
                  to="/"
                  className="hidden shrink-0 items-center gap-2.5 lg:flex"
                  aria-label="Neo console home"
                >
                  <span className="flex flex-col items-start leading-none">
                    <img
                      className="h-[20px] w-auto"
                      src="/images/neo-logo/neo-logo.svg"
                      alt="Neo"
                    />
                    <span className="mt-1 text-[10px] text-[var(--mute)]">by Nethority</span>
                  </span>
                </Link>
                <span className="mx-1 hidden h-[21px] w-px shrink-0 bg-[var(--rule)] lg:block" />
                {/* Brand - mobile and tablet */}
                <Link
                  to="/"
                  className="absolute left-1/2 top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 lg:hidden"
                  aria-label="Neo console home"
                >
                  <span className="flex flex-col items-center leading-none">
                    <img
                      className="h-[18px] w-auto"
                      src="/images/neo-logo/neo-logo.svg"
                      alt="Neo"
                    />
                    <span className="mt-1 text-[9px] text-[var(--mute)]">by Nethority</span>
                  </span>
                </Link>
              </>
            )}
            <button
                  className="z-99999 flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full border border-[var(--rule)] text-[var(--mute)] transition-colors hover:bg-[var(--band)] hover:text-[var(--ink)]"
                  onClick={handleToggle}
                  aria-label="Toggle Sidebar"
                >
                  {isMobileOpen ? (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M6.21967 7.28131C5.92678 6.98841 5.92678 6.51354 6.21967 6.22065C6.51256 5.92775 6.98744 5.92775 7.28033 6.22065L11.999 10.9393L16.7176 6.22078C17.0105 5.92789 17.4854 5.92788 17.7782 6.22078C18.0711 6.51367 18.0711 6.98855 17.7782 7.28144L13.0597 12L17.7782 16.7186C18.0711 17.0115 18.0711 17.4863 17.7782 17.7792C17.4854 18.0721 17.0105 18.0721 16.7176 17.7792L11.999 13.0607L7.28033 17.7794C6.98744 18.0722 6.51256 18.0722 6.21967 17.7794C5.92678 17.4865 5.92678 17.0116 6.21967 16.7187L10.9384 12L6.21967 7.28131Z"
                        fill="currentColor"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 16 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0.583252 1C0.583252 0.585788 0.919038 0.25 1.33325 0.25H14.6666C15.0808 0.25 15.4166 0.585786 15.4166 1C15.4166 1.41421 15.0808 1.75 14.6666 1.75L1.33325 1.75C0.919038 1.75 0.583252 1.41422 0.583252 1ZM0.583252 11C0.583252 10.5858 0.919038 10.25 1.33325 10.25L14.6666 10.25C15.0808 10.25 15.4166 10.5858 15.4166 11C15.4166 11.4142 15.0808 11.75 14.6666 11.75L1.33325 11.75C0.919038 11.75 0.583252 11.4142 0.583252 11ZM1.33325 5.25C0.919038 5.25 0.583252 5.58579 0.583252 6C0.583252 6.41421 0.919038 6.75 1.33325 6.75L7.99992 6.75C8.41413 6.75 8.74992 6.41421 8.74992 6C8.74992 5.58579 8.41413 5.25 7.99992 5.25L1.33325 5.25Z"
                        fill="currentColor"
                      />
                    </svg>
                  )}
                </button>
            {/* {standaloneBar
            ? (() => {
                const days = currentSubscription?.trial_days_remaining;
                if (typeof days === "number" && days >= 0 && days <= 7) {
                  return (
<div className="min-w-0 flex-1 text-center text-[11px] font-bold text-[var(--accent2)] animate-pulse dark:text-[var(--accent2)]">
                      Your free trial expires in {days} days
</div>
                  );
                }
                return null;
              })()
            : null} */}
            {/* {standaloneBar
              ? (() => {
                const days = currentSubscription?.trial_days_remaining;
                const isTrialEnded = days === 0;

                if (typeof days === "number" && days >= 0 && days <= 7) {
                  return (
                    <div
                      className={`min-w-0 flex-1 text-center text-[11px] font-bold transition-all ${isTrialEnded
                        ? "text-red-600 dark:text-red-400"
                        : "text-[var(--accent2)] animate-pulse dark:text-[var(--accent2)]"
                        }`}
                    >
                      {isTrialEnded
                        ? "Your trial period has ended. "
                        : `Your free trial expires in ${days} day${days !== 1 ? "s" : ""
                        }. `}

                      <span
                        onClick={() => navigate("/billing")}
                        className="cursor-pointer underline hover:opacity-80"
                      >
                        Upgrade now
                      </span>
                    </div>
                  );
                }

                return null;
              })()
              : null} */}
              {standaloneBar
                ? (() => {
                  const days = currentSubscription?.trial_days_remaining;
                  const isTrialEnded = days === 0;

                  if (!isPaidSubscription && typeof days === "number" && days >= 0 && days <= 7) {
                    return (
                      <div
                        className={`min-w-0 flex-1 text-center text-[11px] font-bold transition-all ${isTrialEnded
                          ? "text-red-600 dark:text-red-400"
                          : "text-[var(--accent2)] animate-pulse dark:text-[var(--accent2)]"
                          }`}
                      >
                        {isTrialEnded
                          ? "Your trial period has ended. "
                          : `Your free trial expires in ${days} day${days !== 1 ? "s" : ""
                          }. `}

                        <span
                          onClick={() => navigate("/billing")}
                          className="cursor-pointer underline hover:opacity-80"
                        >
                          Upgrade now
                        </span>
                      </div>
                    );
                  }

                  return null;
                })()
                : null}

            {/* Search - hidden */}
            <div className="hidden">
              <form>
                <div className="relative">
                  <span className="absolute -translate-y-1/2 pointer-events-none left-4 top-1/2">
                    <svg
                      className="fill-gray-500 dark:fill-gray-400"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                        fill=""
                      />
                    </svg>
                  </span>
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Search or type command..."
                    className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-14 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-800 dark:bg-gray-900 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 xl:w-[430px]"
                  />
                  <button className="absolute right-2.5 top-1/2 inline-flex -translate-y-1/2 items-center gap-0.5 rounded-lg border border-gray-200 bg-gray-50 px-[7px] py-[4.5px] text-xs -tracking-[0.2px] text-gray-500 dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-400">
                    <span> ⌘ </span>
                    <span> K </span>
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Right-side actions */}
          <div className="flex items-center gap-1.5 shrink-0 sm:gap-2 2xsm:gap-3">
            {/* ─── iSight Intelligence Button ───────────────────────────────── */}
            <div className="relative hidden">
              <button
                id="isight-open-btn"
                onClick={handleOpenIsight}
                title={`Open iSight Intelligence for ${currentShop || "your store"}`}
                className="flex items-center gap-2 px-3 py-2 rounded-lg font-semibold text-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: "var(--btngrad)",
                  color: "var(--onaccent)",
                  border: "none",
                  boxShadow: "var(--btnglow)",
                }}
              >
                <>
                  <img
                    src={`/images/isightlogo/isight-white.svg`}
                    alt="iSight"
                    className="w-20 hidden dark:block"
                  />
                  <img
                    src={`/images/isightlogo/isight-black.svg`}
                    alt="iSight Dark"
                    className="w-20 dark:hidden"
                  />
                </>
              </button>

              {/* Error tooltip */}
              {isightError && (
                <div
                  className="absolute top-full right-0 mt-2 z-50 px-3 py-2 rounded-lg text-xs text-white shadow-lg"
                  style={{
                    background: "var(--crit)",
                    minWidth: "200px",
                    maxWidth: "300px",
                    whiteSpace: "normal",
                  }}
                >
                  {isightError}
                  <button
                    onClick={() => setIsightError(null)}
                    className="ml-2 opacity-70 hover:opacity-100"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
            {/* ──────────────────────────────────────────────────────────────── */}
            {/* <button
            onClick={() => setShowPopup(true)}
            className="flex items-center space-x-2 px-4 py-2 text-white bg-primary rounded-lg transition-colors hidden md:block"
>
            Book a demo
</button> */}{" "}
            {/* <div className="w-2 h-2 rounded-full bg-[var(--accent)] shadow-[0_0_8px_var(--accent)]"></div> */}
            {/* <div className="hidden sm:flex md:block cursor-pointer items-center gap-2 px-3.5 py-1.5 bg-[var(--accent)]/10 border border-[var(--accent)]/30 rounded-full text-[var(--accent)] text-[13px] font-semibold font-[Inter,sans-serif]">
<span className="font-bold">+ Credits:</span>
<span className="font-bold">
              {availableCredits !== null ? availableCredits : 0}
</span>
</div> */}
            {/* <NotificationBell /> */}
            {/* {hasConnectedStore && (
              <Link to="/billing">
                <div className="hidden lg:flex cursor-pointer items-center gap-2 px-3 py-1 bg-[var(--accent)]/10 border border-[var(--accent)]/30 rounded-full text-[var(--accent)] text-[13px] font-semibold font-[Inter,sans-serif] hover:bg-[var(--accent)]/20 transition whitespace-nowrap">
                  <span className="font-bold">+ Credits:</span>
                  <span className="font-bold">
                    {availableCredits !== null ? availableCredits : 0}
                  </span>
                </div>
              </Link>
            )} */}
            {/* Store Switcher - Show for all users with connected stores */}
            {hasConnectedStore && hasMultipleStores && (
              <StoreSwitcher compact={true} className="lg:block" />
            )}
            {/* Pending Invitations - Show for member users with pending invitations */}
            {/* {isMemberUser && hasAnyStore && (
<PendingInvitationsDropdown 
              className="hidden lg:block" 
            />
          )} */}
            {/* Dark Mode Toggler */}
            <ThemeToggleButton />
            {/* User Area */}
            <UserDropdown
              disableNavigation={disableNavigation}
              availableCredits={availableCredits}
            />
          </div>
        </div>

        {/* Calendly Book a Demo Modal */}
        {/* {showPopup && (
<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-99999" style={{ marginTop: '0px' }}>
<div className="w-full max-w-4xl max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)] md:max-h-[calc(100vh-1rem)] min-h-[75vh] md:min-h-[90vh] rounded-lg bg-white shadow-xl">
<div className="flex items-center justify-between z-50 relative">
<div className="flex items-center gap-4">
<h2 className="text-xl p-4 font-bold">
                  Pick your time slot for expert assistance
</h2>
</div>
<button
                onClick={() => setShowPopup(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
>
<svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
</svg>
</button>
</div>
<div className="">
<iframe
                className="calendly-inline-widget w-full h-screen"
                src="https://calendly.com/netsightai/netsights-ai-support?hide_event_type_details=1&hide_gdpr_banner=1&background_color=#ffffff&text_color=000000&primary_color=40bfb9%22
                data-url="https://calendly.com/netsightai/netsights-ai-support?hide_event_type_details=1&hide_gdpr_banner=1&background_color=1a1a1a&text_color=ffffff&primary_color=40bfb9%22
              />
<script type="text/javascript" src="https://assets.calendly.com/assets/external/widget.js" async />

 
            </div>
</div>
</div>
      )} */}
      </header>

      <TrialExpiryModal
        open={showTrialModal}
        onClose={() => setShowTrialModal(false)}
        daysRemaining={currentSubscription?.trial_days_remaining ?? 0}
        isExpired={currentSubscription?.trial_days_remaining === 0}
      />

    </>
  );
};

export default AppHeader;